import { Scissors, Layers, Percent, Ruler } from "lucide-react";

// Renders the CNC nesting layout on 210x280 sheets as SVG.
export function CncPlan({ cnc }) {
  const { sheets, firePercent } = cnc;

  const metrics = [
    { icon: Layers, label: "Tabaka Adedi", value: `${cnc.sheetCount} adet` },
    { icon: Ruler, label: "Kullanılan Alan", value: `${cnc.usedAreaM2} m²` },
    { icon: Scissors, label: "Kesim Metrajı", value: `${cnc.cutLengthM} m` },
    { icon: Layers, label: "Parça Sayısı", value: `${cnc.partCount} parça` },
  ];

  const fireHigh = firePercent > 45;

  return (
    <div className="card-atelier rounded-2xl p-6" data-testid="cnc-plan">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Scissors className="w-5 h-5 text-primary" />
          <h3 className="font-serif text-2xl font-bold text-stone-900">CNC Kesim Planı</h3>
          <span className="text-xs font-mono text-stone-500 ml-1">
            {cnc.combined
              ? "Gövde & Kapak 2.10×2.80m (aynı malzeme & renk)"
              : `Gövde 2.10×2.80m • Kapak ${(cnc.doorSheetH / 100).toFixed(2)}×${(cnc.doorSheetW / 100).toFixed(2)}m`}
          </span>
        </div>
        <div
          data-testid="cnc-fire-percentage"
          className="flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold"
          style={{ background: fireHigh ? "#FEE2E2" : "#D1FAE5", color: fireHigh ? "#DC2626" : "#059669" }}
        >
          <Percent className="w-4 h-4" />
          Fire Oranı: %{firePercent}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {metrics.map((m, i) => (
          <div key={i} className="rounded-xl bg-[#F5F0EB] p-3 border border-[#E6DFD5]">
            <div className="flex items-center gap-1.5 text-stone-500 mb-1">
              <m.icon className="w-3.5 h-3.5" />
              <span className="text-[11px] font-mono uppercase tracking-wide">{m.label}</span>
            </div>
            <p className="text-base font-bold text-stone-900">{m.value}</p>
          </div>
        ))}
      </div>

      {cnc.glassCount > 0 && (
        <p data-testid="cnc-glass-note" className="text-xs text-stone-500 mb-4 -mt-2">
          {cnc.glassCount} cam kapak (siyah alüminyum çerçeveli) tabaka kesim planına dahil değildir — çerçeve profil ve cam ayrıca temin edilir.
        </p>
      )}
      {cnc.mirrorCount > 0 && (
        <p data-testid="cnc-mirror-note" className="text-xs text-stone-500 mb-4 -mt-2">
          {cnc.mirrorCount} ayna kapak tabaka kesim planına dahil değildir — ayna ayrıca temin edilir.
        </p>
      )}

      <div className="space-y-5">
        {sheets.map((sheet, si) => {
          const pad = 6;
          const sw = sheet.w;
          const sh = sheet.h;
          return (
            <div key={si}>
              <p className="text-xs font-mono text-stone-500 mb-2">
                Tabaka #{si + 1} — {sheet.group === "door" ? "Kapak" : sheet.group === "mixed" ? "Gövde & Kapak" : "Gövde"} • {sh} × {sw} cm
              </p>
              <div className="rounded-xl overflow-hidden border border-stone-700 bg-stone-900">
                <svg
                  data-testid={`cnc-layout-canvas-${si}`}
                  viewBox={`${-pad} ${-pad} ${sw + pad * 2} ${sh + pad * 2}`}
                  className="w-full"
                  style={{ display: "block" }}
                >
                  <rect x={0} y={0} width={sw} height={sh} fill="#262626" stroke="#525252" strokeWidth={0.8} />
                  {sheet.placements.map((p, i) => (
                    <g key={i}>
                      <rect
                        x={p.x}
                        y={p.y}
                        width={p.w}
                        height={p.h}
                        fill={p.color}
                        fillOpacity={0.92}
                        stroke="#FDFBF7"
                        strokeWidth={0.7}
                        rx={0.8}
                      />
                      <text
                        x={p.x + p.w / 2}
                        y={p.y + p.h / 2}
                        fontSize={Math.min(9, Math.max(5, p.w / 8))}
                        fill="#1c1917"
                        fontWeight="600"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fontFamily: "JetBrains Mono, monospace" }}
                      >
                        {p.name}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-stone-500">
        <span className="inline-block w-3 h-3 rounded-sm bg-[#262626] border border-stone-500" />
        Fire (kullanılmayan alan)
        <span className="inline-block w-3 h-3 rounded-sm bg-primary ml-3" />
        Kesilecek parçalar
      </div>

      {cnc.glassPanels && cnc.glassPanels.length > 0 && (
        <div className="mt-6" data-testid="cnc-glass-section">
          <p className="text-xs font-mono uppercase tracking-wide text-stone-500 mb-3">
            Cam Kapak Ölçüleri — Siyah Alüminyum Çerçeveli
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {cnc.glassPanels.map((g, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden border border-stone-700 bg-stone-900 p-3"
                data-testid={`cnc-glass-door-${i}`}
              >
                <svg
                  viewBox={`-8 -8 ${g.w + 16} ${g.h + 16}`}
                  className="w-full"
                  style={{ display: "block", maxHeight: 220 }}
                >
                  <rect x={0} y={0} width={g.w} height={g.h} fill="#161616" stroke="#6b7280" strokeWidth={1.2} />
                  <rect x={4.5} y={4.5} width={g.w - 9} height={g.h - 9} fill="#9fb6bf" fillOpacity={0.35} stroke="#8fa8b2" strokeWidth={0.5} />
                  <text
                    x={g.w / 2}
                    y={g.h / 2 - 6}
                    fontSize={7}
                    fill="#F5F5F4"
                    fontWeight="700"
                    textAnchor="middle"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                  >
                    {g.name}
                  </text>
                  <text
                    x={g.w / 2}
                    y={g.h / 2 + 6}
                    fontSize={6}
                    fill="#D6D3D1"
                    textAnchor="middle"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                  >
                    Cam: {g.glassW} × {g.glassH} cm
                  </text>
                </svg>
                <p className="text-[11px] font-mono text-stone-300 mt-2 text-center">
                  Kapak: {g.w} × {g.h} cm • Cam: {g.glassW} × {g.glassH} cm
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {cnc.mirrorPanels && cnc.mirrorPanels.length > 0 && (
        <div className="mt-6" data-testid="cnc-mirror-section">
          <p className="text-xs font-mono uppercase tracking-wide text-stone-500 mb-3">
            Ayna Kapak Ölçüleri
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {cnc.mirrorPanels.map((g, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden border border-stone-700 bg-stone-900 p-3"
                data-testid={`cnc-mirror-door-${i}`}
              >
                <svg
                  viewBox={`-8 -8 ${g.w + 16} ${g.h + 16}`}
                  className="w-full"
                  style={{ display: "block", maxHeight: 220 }}
                >
                  <rect x={0} y={0} width={g.w} height={g.h} fill="#c9d4da" stroke="#94a3b8" strokeWidth={1.2} />
                  <text
                    x={g.w / 2}
                    y={g.h / 2 - 6}
                    fontSize={7}
                    fill="#1c1917"
                    fontWeight="700"
                    textAnchor="middle"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                  >
                    {g.name}
                  </text>
                  <text
                    x={g.w / 2}
                    y={g.h / 2 + 6}
                    fontSize={6}
                    fill="#44403c"
                    textAnchor="middle"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                  >
                    Ayna: {g.w} × {g.h} cm
                  </text>
                </svg>
                <p className="text-[11px] font-mono text-stone-300 mt-2 text-center">
                  Kapak: {g.w} × {g.h} cm • Ayna tam kapak ölçüsünde
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {cnc.unplaced && cnc.unplaced.length > 0 && (
        <div
          data-testid="cnc-unplaced-parts"
          className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4"
        >
          <p className="text-sm font-semibold text-amber-800 mb-2">
            Tabakaya Sığmayan Parçalar (standart tabaka ölçüsü yetmiyor)
          </p>
          <ul className="space-y-1">
            {cnc.unplaced.map((p, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-amber-800 font-mono">
                <span
                  className="inline-block w-3 h-3 rounded-sm border border-amber-400"
                  style={{ background: p.color }}
                />
                {p.name} — {p.w} × {p.h} cm
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
